import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList
} from 'recharts';
import { useTheme } from '@material-ui/styles';
import { getComfortRanges } from './utils';

interface Props {
  data: any;
  isAnimationActive?: boolean;
  bigLabels?: boolean;
}

const ComfortRanges = ({ data }: Props) => {
  const theme = useTheme() as any;
  const [currentBar, setCurrentBar] = useState('');
  const isData = data && data.length > 0;

  const customLabels = getComfortRanges(
    theme.palette.baseColors.charts['risk-analysis-ms']
  );

  const dataToRender = isData
    ? data.map((item: any) => {
        let cumulativeStart = 0;
        const processedItem: any = { name: item.name };
        const labelNames = customLabels.map(
          (label: any) => label?.key || label.label
        );

        Object.keys(item).forEach(key => {
          if (labelNames.includes(key)) {
            const value = item[key] as number;
            processedItem[key] = value;
            const range = value
              ? `${cumulativeStart === 0 ? 0 : cumulativeStart + 1} - ${cumulativeStart + value}`
              : null;

            processedItem[`${key}_range`] = value && value > 5 ? range : null;
            processedItem[`${key}_range_text`] = value ? range : null;
            cumulativeStart += value;
          }
        });

        return processedItem;
      })
    : [];

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active) return;
    for (const bar of payload) {
      if (bar.dataKey === currentBar) {
        return (
          <div
            style={{
              backgroundColor: '#fff',
              padding: '10px',
              boxShadow:
                '0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)'
            }}
          >
            {bar.name}: {bar.payload[`${currentBar}_range_text`]}
          </div>
        );
      }
    }
    return null;
  };

  const chartHeight = Math.max(dataToRender.length, 1.75) * 70 + 100;

  const wrapSvgText = (text: string, maxWidth: number, svgFontSize = 14) => {
    if (!text) return [];

    const words = text.split(' ');
    const lines = [];
    let line = '';

    const measureText = (str: string) => str.length * (svgFontSize * 0.6); // Approximate width

    for (const word of words) {
      const testLine = line ? `${line} ${word}` : word;
      if (measureText(testLine) > maxWidth) {
        lines.push(line);
        line = word;
      } else {
        line = testLine;
      }
    }
    lines.push(line);

    return lines;
  };

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart
        layout="vertical"
        data={dataToRender}
        barSize={50}
        margin={{ top: 80, right: 50, left: 50, bottom: 10 }}
      >
        <CartesianGrid vertical={false} strokeDasharray="3 3" />

        {/* Single X-Axis with custom labels and percentages */}
        <XAxis
          type="number"
          domain={[0, 100]}
          orientation="top" // Place ticks at the bottom of the chart
          ticks={[0, 25, 50, 75, 100]} // Ticks at 0%, 25%, 50%, 75%, 100%
          tick={({ x, y, payload }) => {
            // Find the custom label that corresponds to the current tick
            const label = customLabels.find(
              item => item.value === payload.value
            );

            if (!label) return <></>; // If no matching label, render nothing

            // Render the custom label directly above the tick
            return (
              <>
                {/* Custom label above the tick */}
                <text
                  x={x}
                  y={y - 40} // Adjust the y position to move the label above the tick
                  fill={label.color}
                  fontSize={15}
                  textAnchor="middle" // Center the label horizontally above the tick
                  fontWeight={600}
                >
                  {label.label}
                </text>

                {/* Conditionally render the percentage label */}
                {(payload.value === 0 ||
                  payload.value === 50 ||
                  payload.value === 100) && (
                  <text
                    x={x}
                    y={y - 10} // Move the percentage label further below the tick to create more space
                    fill="#000" // You can customize this color
                    fontSize={15}
                    textAnchor="middle" // Center the percentage below the custom label
                  >
                    {payload.value}
                  </text>
                )}
              </>
            );
          }}
        />

        <svg width={300} height={90} x={130} y={90}>
          <text
            x={0}
            y={15}
            textAnchor="middle"
            fontWeight="bold"
            fontSize="12"
          >
            <tspan x={170} dy={0}>
              Growth
            </tspan>
            <tspan x={170} dy={14}>
              Assets
            </tspan>
          </text>
        </svg>

        <YAxis
          type="category"
          dataKey="name"
          width={300}
          tickLine={false}
          tickMargin={20}
          tick={({ x, y, payload }) => {
            const { value } = payload;
            const growthAsset = data.find(
              (item: any) => item.name === value
            )?.growth_assets;
            const wrappedValue = wrapSvgText(value, 280);

            return (
              <g transform={`translate(${x},${y})`}>
                {/* Render the first column (name) */}
                <text x={-80} y={0} dy={4} textAnchor="end" fill="#000">
                  {wrappedValue.map((line, i) => (
                    <tspan key={i} x={-80} dy={i === 0 ? 0 : '1.2em'}>
                      {line}
                    </tspan>
                  ))}
                </text>
                {/* Render the second column (growth_asset) */}
                <text x={-38} y={0} dy={4} textAnchor="start" fill="#666">
                  {growthAsset ? `${parseInt(growthAsset)}%` : '-'}
                </text>
              </g>
            );
          }}
        />

        <Tooltip wrapperStyle={{ zIndex: 9 }} content={CustomTooltip} />

        {customLabels.map((label: any, index: number) => {
          const key = label?.key || label.label;
          return (
            <Bar
              key={index}
              dataKey={key}
              onMouseOver={() => setCurrentBar(key)}
              stackId="a"
              fill={label.color}
            >
              {dataToRender.map((entry: any, index: number) => {
                let radius = 0;
                let maxKey = '';

                customLabels.forEach((label: any) => {
                  const barKey = label?.key || label.label;
                  if (entry[barKey] !== null) {
                    maxKey = barKey;
                  }
                });

                if (maxKey === key) {
                  radius = 25;
                }

                return (
                  // @ts-ignore
                  <Cell key={`cell-${index}`} radius={[0, radius, radius, 0]} />
                );
              })}
              <LabelList
                dataKey={`${key}_range`}
                position="inside"
                fill="#fff"
                fontSize={14}
                fontWeight="bold"
              />
            </Bar>
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default ComfortRanges;
